import { Divider, Tabs, Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { forwardRef, ReactElement, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { COMPANY_ROUTES } from '../../../Base.constants';
import useFetchRiskProducts from '../../../hooks/riskProducts/useFetchRiskProducts';
import { Chart } from '../../../models/RiskProducts';
import { useCompanyRiskProductsContext } from '../../../pages/Company/CompanyRiskProductsContext';
import { getCompanyId } from '../../../utils/tagUtils';
import CqsChart from './CqsChart/CqsChart';
import { hasCqsMetrics } from './CqsChart/CqsChart.logic';
import CreChart from './CreChart/CreChart';
import { hasCreMetrics } from './CreChart/CreChart.logic';
import ErrorBoundary from '../../ErrorBoundary';
import { hasCompanyRiskMetrics } from './RiskProductsWidget.logic';
import styles from './RiskProductsWidget.module.scss';
import { RiskProductsProps } from './RiskProductsWidget.types';

const cx = classNames.bind(styles);

const riskProductsWebsite: string = 'http://rp.creditsights.com/';
const HeaderLink = ({ children }) => <Link to={COMPANY_ROUTES.risk}>{children}</Link>;

const RiskProductsWidget = (
  { companyTag, print, dynamicHeader = false, ...sortableWidgetProps }: RiskProductsProps,
  ref
): ReactElement | null => {
  const companyId = getCompanyId(companyTag);
  const { data: riskMetrics, isLoading, isError } = useFetchRiskProducts(companyId);

  const { cqsMetrics, creMetrics, modelId, regionId } = riskMetrics;
  const { activeTab, setActiveTab } = useCompanyRiskProductsContext();
  const cqsMetricsExist = hasCqsMetrics(cqsMetrics);
  const creMetricsExist = hasCreMetrics(creMetrics);
  const companyRiskMetricsExists = hasCompanyRiskMetrics(cqsMetrics, creMetrics);

  const tabData = [
    ...(cqsMetricsExist
      ? [
          {
            active: activeTab === Chart.Cqs,
            onClick: () => setActiveTab(Chart.Cqs),
            label: 'CQS',
            dataTestId: 'cqs-chart-tab',
          },
        ]
      : []),
    ...(creMetricsExist
      ? [
          {
            active: activeTab === Chart.Cre,
            onClick: () => setActiveTab(Chart.Cre),
            label: 'CRE',
            dataTestId: 'cre-chart-tab',
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (activeTab !== null) {
      return;
    }

    if (cqsMetricsExist) {
      setActiveTab(Chart.Cqs);
    } else {
      setActiveTab(Chart.Cre);
    }
  }, [cqsMetricsExist]);

  const isTabShown = (label: string): boolean => tabData.filter((tab) => tab.label === label).length > 0;

  const renderChart = () => {
    if (isTabShown('CQS') && activeTab === Chart.Cqs) {
      return <CqsChart cqsMetrics={cqsMetrics} />;
    }
    if (isTabShown('CRE') && activeTab === Chart.Cre) {
      return <CreChart modelId={modelId} regionId={regionId} creMetrics={creMetrics} />;
    }
    return null;
  };

  const renderHeaderTitle = () => {
    if (!dynamicHeader) {
      return 'Risk Products';
    }
    return activeTab === Chart.Cqs ? 'CQS History' : 'CRE History';
  };

  if (!companyRiskMetricsExists) {
    return null;
  }

  return (
    <Widget
      headerTitle={renderHeaderTitle()}
      headerLink={print ? undefined : HeaderLink}
      firstLine={<Tabs tabs={tabData} />}
      dataTestId="risk-products"
      ref={ref}
      isLoading={isLoading}
      isError={isError}
      {...sortableWidgetProps}
    >
      <div className={cx('body', { print })} data-testid="risk-products-chart-wrapper">
        <ErrorBoundary onRetryClick={() => {}}>
          <>
            {renderChart()}
            <div className={cx('divider-wrapper')}>
              <Divider />
            </div>
            <div className={cx('description')}>
              <p>
                CreditSights Risk Products is a suite of quantitative credit risk models designed to help you monitor
                and manage risk across your portfolios. To access the full set of risk metrics across our entire covered
                universe of more than 7000 companies{' '}
                <a
                  href={riskProductsWebsite}
                  className={cx('link')}
                  rel="noopener noreferrer"
                  target="_blank"
                  aria-label="Read more about full set of risk metrics"
                  data-testid="risk-products-source-link"
                >
                  click here
                </a>
                .
              </p>
              <p>
                Credit Quality Score (CQS), a medium-term measure of credit risk. Credit Risk Estimate (CRE), is a
                one-year forward probability of default.
              </p>
            </div>
          </>
        </ErrorBoundary>
      </div>
    </Widget>
  );
};

export default forwardRef(RiskProductsWidget);
